import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, Users, Check } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TableCalendarPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthData, setMonthData] = useState({});
  const [daySlots, setDaySlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    fetchMonthData();
  }, [currentDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchDayAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchMonthData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/availability/calendar/${year}/${month}`);
      setMonthData(response.data.days);
    } catch (error) {
      console.error('Error fetching month data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDayAvailability = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/availability/${dateStr}`);
      setDaySlots(response.data.slots);
    } catch (error) {
      console.error('Error fetching day availability:', error);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getDateString = (day) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-${String(day).padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'full': return 'bg-red-100 text-red-600 border-red-200';
      case 'busy': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100';
    }
  };

  const isPastDate = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return checkDate < today;
  };

  const handleBookSlot = (time) => {
    navigate(`/booking?date=${selectedDate}&time=${time}`);
  };

  return (
    <div className="min-h-screen pt-28 pb-16 bg-gradient-to-b from-silver-bright to-white" data-testid="calendar-page">
      {/* Hero Banner */}
      <section className="page-hero mb-8">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1715782414430-e1f0f4df354e?w=1920&q=80"
            alt="Table availability"
            className="w-full h-full object-cover"
          />
          <div className="page-hero-overlay" />
        </div>
        <div className="relative z-10 container-elegant">
          <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
            Plan Your Visit
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Table Availability
          </h1>
          <p className="text-gray-200 text-lg max-w-xl">
            Check available time slots and book your perfect dining experience
          </p>
        </div>
      </section>

      <div className="container-elegant">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="card-elegant p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button
                  variant="ghost"
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="font-serif text-xl text-gray-800">
                  {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {days.map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, index) => {
                  if (!day) {
                    return <div key={index} className="h-14" />;
                  }
                  
                  const dateStr = getDateString(day);
                  const dayData = monthData[dateStr];
                  const isPast = isPastDate(day);
                  const isSelected = selectedDate === dateStr;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => !isPast && setSelectedDate(dateStr)}
                      disabled={isPast}
                      className={`h-14 rounded-lg border text-sm font-medium transition-all ${
                        isPast 
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed border-gray-100'
                          : isSelected
                            ? 'bg-malva text-white border-malva-dark ring-2 ring-malva/30'
                            : dayData
                              ? getStatusColor(dayData.status)
                              : 'bg-white text-gray-700 border-gray-200 hover:border-malva'
                      }`}
                    >
                      {day}
                      {dayData && !isPast && (
                        <span className="block text-xs opacity-70">
                          {dayData.booking_count > 0 ? `${dayData.booking_count} booked` : ''}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-200"></div>
                  <span className="text-sm text-gray-600">Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200"></div>
                  <span className="text-sm text-gray-600">Busy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
                  <span className="text-sm text-gray-600">Full</span>
                </div>
              </div>
            </div>
          </div>

          {/* Time Slots */}
          <div>
            <div className="card-elegant p-6 sticky top-28">
              <h3 className="font-serif text-lg text-gray-800 mb-4">
                {selectedDate ? (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-malva" />
                    {selectedDate}
                  </span>
                ) : (
                  'Select a Date'
                )}
              </h3>

              {selectedDate ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-4">Available time slots:</p>
                  {daySlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {daySlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && handleBookSlot(slot.time)}
                          disabled={!slot.available}
                          className={`p-3 rounded-lg text-sm font-medium transition-all ${
                            slot.available
                              ? 'bg-malva/10 text-malva-dark hover:bg-malva/20 border border-malva/20'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <Clock className="w-4 h-4 inline mr-1" />
                          {slot.time}
                          {slot.available && (
                            <span className="block text-xs mt-1 opacity-70">
                              {slot.remaining_capacity} seats
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Loading...</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Click on a date to see available time slots</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableCalendarPage;
